/**
 * WordPress dependencies
 */
import { useSelect } from '@safe-wordpress/data';
import { dateI18n } from '@safe-wordpress/date';
import { createInterpolateElement } from '@safe-wordpress/element';
import { _x, sprintf } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { store as NAB_DATA } from '@nab/data';
import { formatNumber } from '@nab/i18n';
import type { Quota } from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';
import { CircularProgress } from '../circular-progress';

export function Quota(): JSX.Element {
	const data = useQuotaData();
	const isSubscribed = useIsSubscribed();

	const value = data.status === 'ready' ? data.quota.percentage : '–';
	const percentage = data.status === 'ready' ? data.quota.percentage : 0;

	const expirationDate =
		data.status === 'ready' ? data.quota.boostExpiration : undefined;
	const isBoostActive = expirationDate && ! isDateOver( expirationDate );

	const [ color, title, explanation ] = getLabels( data, isSubscribed );

	const expiration = isToday( expirationDate ?? '' )
		? _x( 'Ends today', 'text', 'nelio-ab-testing' )
		: sprintf(
				/* translators: %s: Date. */
				_x( 'Ends on %s', 'text', 'nelio-ab-testing' ),
				dateI18n(
					_x( 'F j', 'date wihout year', 'nelio-ab-testing' ),
					expirationDate ?? ''
				)
		  );

	const maybeBoostEndingLabel = isBoostActive
		? ` <em>(${ expiration })</em>`
		: '';

	return (
		<div className="nab-overview-available-quota">
			<h3 className="nab-overview-component-title">
				{ isBoostActive ? (
					<>
						<span className="nab-overview-available-quota__boost-icon">
							🚀
						</span>
						{ ' ' +
							_x(
								'Available Quota during Activation Boost',
								'text',
								'nelio-ab-testing'
							) }
					</>
				) : (
					_x( 'Available Quota', 'text', 'nelio-ab-testing' )
				) }
			</h3>
			<CircularProgress
				color={ color }
				value={ value || '⚠️' }
				percentage={ percentage }
				showPercent={
					'ready' === data.status && !! data.quota.availableQuota
				}
				title={ title }
				explanation={ createInterpolateElement(
					explanation + maybeBoostEndingLabel,
					{
						strong: <strong />,
						em: <em />,
					}
				) }
			/>
		</div>
	);
}

// =====
// HOOKS
// =====

type QuotaData =
	| { status: 'loading' | 'error' }
	| { quota: Quota; status: 'ready' };

const useQuotaData = () =>
	useSelect( ( select ): QuotaData => {
		/* eslint-disable @wordpress/no-unused-vars-before-return */
		const quota = select( NAB_DATA ).getQuota();
		const isLoading =
			! select( NAB_DATA ).hasFinishedResolution( 'getQuota' );
		const isError = select( NAB_DATA ).hasResolutionFailed( 'getQuota' );
		/* eslint-enable @wordpress/no-unused-vars-before-return */

		if ( isLoading ) {
			return { status: 'loading' };
		}

		if ( isError || ! quota ) {
			return { status: 'error' };
		}

		return { quota, status: 'ready' };
	}, [] );

const useIsSubscribed = () =>
	useSelect( ( select ) => select( NAB_DATA ).isSubscribedTo( 'basic' ), [] );

// =======
// HELPERS
// =======

function isDateOver( datetime: string ): boolean {
	const now = new Date().toISOString();
	return datetime <= now;
}

function isToday( datetime: string ): boolean {
	const today = new Date();
	const date = new Date( datetime );
	return (
		date.getFullYear() === today.getFullYear() &&
		date.getMonth() === today.getMonth() &&
		date.getDate() === today.getDate()
	);
}

function getLabels(
	data: QuotaData,
	isSubscribed: boolean
): [ 'disabled' | 'bad' | 'ok' | 'good', string, string ] {
	const quota = data.status === 'ready' ? data.quota.availableQuota : 0;
	const percentage = data.status === 'ready' ? data.quota.percentage : 0;

	if ( 'error' === data.status ) {
		return [
			'bad',
			_x( 'Error', 'text', 'nelio-ab-testing' ),
			_x(
				'There was an error while retrieving your quota. Please try again later.',
				'text',
				'nelio-ab-testing'
			),
		];
	}

	if ( 'ready' !== data.status ) {
		return [
			'disabled',
			_x( 'Loading…', 'text', 'nelio-ab-testing' ),
			_x(
				'Loading available quota. Please wait…',
				'text',
				'nelio-ab-testing'
			),
		];
	}

	if ( ! quota ) {
		return [
			'bad',
			_x( 'No Quota', 'text', 'nelio-ab-testing' ),
			_x( 'You run out of quota.', 'text (quota)', 'nelio-ab-testing' ) +
			' ' +
			isSubscribed
				? _x(
						'Consider purchasing more quota or upgrade your subscription to avoid interruptions.',
						'user',
						'nelio-ab-testing'
				  )
				: _x(
						'Consider subscribing to Nelio A/B Testing to avoid interruptions.',
						'user',
						'nelio-ab-testing'
				  ),
		];
	}

	if ( percentage <= 15 ) {
		return [
			'bad',
			_x( 'Low on Quota', 'text', 'nelio-ab-testing' ),
			sprintf(
				/* translators: %s: Number of page views. */
				_x(
					'Only <strong>%s page views</strong> remain.',
					'text (quota)',
					'nelio-ab-testing'
				),
				formatNumber( data.quota.availableQuota )
			) +
			' ' +
			isSubscribed
				? _x(
						'Consider purchasing more or upgrade your subscription to avoid interruptions.',
						'user',
						'nelio-ab-testing'
				  )
				: _x(
						'Consider subscribing to Nelio A/B Testing to avoid interruptions.',
						'user',
						'nelio-ab-testing'
				  ),
		];
	}

	if ( percentage <= 30 ) {
		return [
			'ok',
			_x( 'Quota Running Low', 'text', 'nelio-ab-testing' ),
			sprintf(
				/* translators: %s: Number of page views. */
				_x(
					'Only <strong>%s page views</strong> remain. You may need to upgrade soon.',
					'text',
					'nelio-ab-testing'
				),
				formatNumber( data.quota.availableQuota )
			),
		];
	}

	return [
		'good',

		_x( 'Plenty of Quota', 'text', 'nelio-ab-testing' ),
		sprintf(
			/* translators: %s: Number of page views. */
			_x(
				'You have <strong>%s page views</strong> available. Get the most out of them!',
				'text',
				'nelio-ab-testing'
			),
			formatNumber( data.quota.availableQuota )
		),
	];
}
